import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useNotifications, useMarkAllRead, useMarkRead } from "../hooks/useNotifications";
import type {
  NotificationDto,
  NotificationListQuery,
  NotificationType,
} from "../services/notificationsApi";
import { NotificationRow } from "../components/NotificationRow";

type ReadFilter = "all" | "unread" | "read";

const ALL_TYPES: { value: NotificationType; label: string }[] = [
  { value: "DiscardApprovalRequested", label: "Discard approval requested" },
  { value: "DiscardApproved", label: "Discard approved" },
  { value: "DiscardRejected", label: "Discard rejected" },
  { value: "SowingInstructionRejected", label: "Sowing instruction rejected" },
  { value: "SowingInstructionAmendmentRequested", label: "Amendment requested" },
  { value: "SowingInstructionAmendmentApproved", label: "Amendment approved" },
  { value: "SowingInstructionAmendmentRejected", label: "Amendment rejected" },
  { value: "SowingWindowReminder", label: "Sowing window reminder" },
  { value: "PlannedSowingReminder", label: "Planned sowing reminder" },
  { value: "PlannedSowingOverdue", label: "Planned sowing overdue" },
  { value: "GerminationOverdue", label: "Germination overdue" },
  { value: "DailySowingPlan", label: "Daily sowing plan" },
  { value: "WeeklySowingPlan", label: "Weekly sowing plan" },
  { value: "StockDepletionAlert", label: "Stock depletion alert" },
  { value: "PaymentPendingApproval", label: "Payment awaiting approval" },
  { value: "PaymentApproved", label: "Payment approved" },
  { value: "PaymentRejected", label: "Payment rejected" },
];

export function NotificationsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [readFilter, setReadFilter] = useState<ReadFilter>("all");
  const [typeFilter, setTypeFilter] = useState<NotificationType | "all">("all");
  const [includeArchived, setIncludeArchived] = useState(false);

  const query = useMemo<NotificationListQuery>(
    () => ({
      pageNumber: page,
      pageSize: 50,
      isRead: readFilter === "all" ? undefined : readFilter === "read",
      types: typeFilter === "all" ? undefined : [typeFilter],
      includeArchived,
    }),
    [page, readFilter, typeFilter, includeArchived]
  );

  const { data, isFetching } = useNotifications(query);
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const items = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  const handleRowClick = async (n: NotificationDto) => {
    if (!n.isRead) {
      try {
        await markRead.mutateAsync(n.id);
      } catch {
        /* navigate regardless */
      }
    }
    if (n.linkUrl) {
      const target = n.linkUrl.startsWith("http") ? new URL(n.linkUrl).pathname : n.linkUrl;
      navigate(target);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Notifications</CardTitle>
          <Button
            variant="outline"
            size="sm"
            disabled={(data?.unreadCount ?? 0) === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            {markAllRead.isPending ? "Marking…" : "Mark all read"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="mb-1 block text-xs">Status</Label>
              <Tabs
                value={readFilter}
                onValueChange={(v) => {
                  setReadFilter(v as ReadFilter);
                  setPage(1);
                }}
              >
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="unread">Unread</TabsTrigger>
                  <TabsTrigger value="read">Read</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div className="w-full sm:w-auto sm:min-w-[14rem]">
              <Label className="mb-1 block text-xs">Type</Label>
              <Select
                value={typeFilter}
                onValueChange={(v) => {
                  setTypeFilter(v as NotificationType | "all");
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {ALL_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 pb-1.5">
              <Checkbox
                id="archived"
                checked={includeArchived}
                onCheckedChange={(v) => {
                  setIncludeArchived(Boolean(v));
                  setPage(1);
                }}
              />
              <Label htmlFor="archived" className="text-sm font-normal">
                Show archived
              </Label>
            </div>
          </div>

          <Separator />

          {isFetching && !data ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No notifications match these filters.
            </div>
          ) : (
            <div className="rounded-md border">
              {items.map((n) => (
                <div key={n.id} className="border-b last:border-b-0">
                  <NotificationRow notification={n} onClick={handleRowClick} />
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {data ? `${data.totalCount} total · page ${data.pageNumber} of ${data.totalPages || 1}` : ""}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
